// src/admin/pages/AdminWholesaleRequestDetails.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminWholesaleRequestById,
  updateAdminWholesaleRequestStatus,
  deleteAdminWholesaleRequest,
} from "../../services/adminWholesaleRequests.api";
import {
  FaUser,
  FaPhone,
  FaInstagram,
  FaStore,
  FaWhatsapp,
  FaTrash,
  FaEdit,
  FaEnvelope,
  FaUserPlus,
} from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";

function AdminWholesaleRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [actionsOpen, setActionsOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const TEXT = {
    EN: {
      title: "Wholesale request",
      overview: "Overview",
      requestInfo: "Request information",
      contactInfo: "Contact information",
      businessInfo: "Business information",
      timeline: "Timeline",
      fullName: "Full name",
      phone: "Phone",
      businessName: "Business name",
      instagramName: "Instagram profile name",
      instagramLink: "Instagram page link",
      status: "Status",
      submittedAt: "Submitted at",
      lastUpdated: "Last updated",
      actions: "More actions",
      changeStatus: "Change status",
      delete: "Delete",
      cancel: "Cancel",
      save: "Save",
      confirm: "Confirm",
      email: "Email",
      addAsCustomer: "Add as customer",
emailButton: "Email",
      deleteTitle: "Delete wholesale request?",
      deleteDesc: "This action cannot be undone.",
      deleted: "Wholesale request deleted",
      statusUpdated: "Wholesale request status updated",
      loading: "Loading wholesale request...",
      new: "New",
      contacted: "Contacted",
      approved: "Approved",
      rejected: "Rejected",
      whatsapp: "WhatsApp",
      instagram: "Instagram",
      openInstagram: "Open Instagram",
      noAccess: "You do not have permission to access this page.",
      requestSince: "Request since",
      requestActivity: "submitted a wholesale account request",
      noInstagram: "No Instagram link",
    },
    AR: {
      title: "طلب جملة",
      overview: "نظرة عامة",
      requestInfo: "معلومات الطلب",
      contactInfo: "معلومات التواصل",
      businessInfo: "معلومات العمل",
      timeline: "السجل",
      fullName: "الاسم الكامل",
      phone: "الهاتف",
      businessName: "اسم العمل",
      instagramName: "اسم حساب الإنستغرام",
      instagramLink: "رابط صفحة الإنستغرام",
      status: "الحالة",
      submittedAt: "تاريخ الإرسال",
      lastUpdated: "آخر تحديث",
      actions: "إجراءات",
      changeStatus: "تغيير الحالة",
      delete: "حذف",
      cancel: "إلغاء",
      save: "حفظ",
      confirm: "تأكيد",
      email: "البريد الإلكتروني",
      addAsCustomer: "إضافة كعميل",
emailButton: "البريد",
      deleteTitle: "حذف طلب الجملة؟",
      deleteDesc: "لا يمكن التراجع عن هذا الإجراء.",
      deleted: "تم حذف طلب الجملة",
      statusUpdated: "تم تحديث حالة طلب الجملة",
      loading: "جاري تحميل طلب الجملة...",
      new: "جديد",
      contacted: "تم التواصل",
      approved: "مقبول",
      rejected: "مرفوض",
      whatsapp: "واتساب",
      instagram: "إنستغرام",
      openInstagram: "فتح الإنستغرام",
      noAccess: "ليس لديك صلاحية للوصول إلى هذه الصفحة.",
      requestSince: "الطلب منذ",
      requestActivity: "أرسل طلب حساب جملة",
      noInstagram: "لا يوجد رابط إنستغرام",
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
  const isAdmin = adminUser.role === "admin";
  const canAccess = isAdmin || perms.can_manage_customers;

  const statusLabel = (status) => {
    const map = {
      new: TEXT.new,
      contacted: TEXT.contacted,
      approved: TEXT.approved,
      rejected: TEXT.rejected,
    };

    return map[status] || status;
  };

  const statusClass = (status) => {
    if (status === "approved") return "bg-green-100 text-green-700";
    if (status === "rejected") return "bg-red-100 text-red-700";
    if (status === "contacted") return "bg-blue-100 text-blue-700";
    return "bg-yellow-100 text-yellow-700";
  };

  const formatDateTime = (value) => {
    if (!value) return "—";

    return new Date(value).toLocaleString(lang === "AR" ? "ar-LB" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSince = (date) => {
    if (!date) return "—";

    const now = new Date();
    const past = new Date(date);
    const diff = now - past;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) {
      return lang === "AR" ? `منذ ${years} سنة` : `since ${years} ${years > 1 ? "years" : "year"}`;
    }

    if (months > 0) {
      return lang === "AR" ? `منذ ${months} شهر` : `since ${months} ${months > 1 ? "months" : "month"}`;
    }

    if (weeks > 0) {
      return lang === "AR" ? `منذ ${weeks} أسبوع` : `since ${weeks} ${weeks > 1 ? "weeks" : "week"}`;
    }

    return lang === "AR" ? `منذ ${days} يوم` : `since ${days} ${days !== 1 ? "days" : "day"}`;
  };

  const getWhatsappLink = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");

    if (!digits) return "#";

    if (digits.startsWith("961")) {
      return `https://wa.me/${digits}`;
    }

    if (digits.startsWith("0") && digits.length === 9) {
      return `https://wa.me/961${digits.slice(1)}`;
    }

    return `https://wa.me/961${digits}`;
  };

  const normalizeInstagramLink = (value) => {
    const v = String(value || "").trim();

    if (!v) return "";

    if (v.startsWith("http://") || v.startsWith("https://")) {
      return v;
    }

    if (v.startsWith("@")) {
      return `https://instagram.com/${v.slice(1)}`;
    }

    return `https://instagram.com/${v}`;
  };

  useEffect(() => {
    if (!canAccess) return;

    setLoading(true);

    getAdminWholesaleRequestById(id)
      .then(setRequest)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, canAccess]);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        !e.target.closest(".actions-overlay") &&
        !e.target.closest(".actions-button")
      ) {
        setActionsOpen(false);
      }
    };

    if (actionsOpen) {
      document.addEventListener("mousedown", handleClick);
    }

    return () => document.removeEventListener("mousedown", handleClick);
  }, [actionsOpen]);

  const handleStatusUpdate = async (status) => {
    if (!request) return;

    setIsSaving(true);

    try {
      await updateAdminWholesaleRequestStatus(request.id, status);

      setRequest((prev) => ({
        ...prev,
        status,
      }));

      setStatusModalOpen(false);

      setToast({
        type: "success",
        message: TEXT.statusUpdated,
      });

      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAsCustomer = () => {
  if (!request) return;

  navigate("/admin/customers/add", {
    state: {
      fromWholesaleRequest: true,
      wholesaleRequestId: request.id,
      customer: {
        name: request.fullName || "",
        phone: request.phone || "",
        email: request.email || "",
        businessName: request.businessName || "",
        instagramName: request.instagramName || "",
        instagramLink: request.instagramLink || "",
      },
    },
  });
};

  const confirmDelete = async () => {
    try {
      await deleteAdminWholesaleRequest(deleteId);

      setToast({
        type: "success",
        message: TEXT.deleted,
      });

      setTimeout(() => {
        navigate("/admin/wholesale-requests");
      }, 1000);
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteId(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex bg-[#f1f1f1] min-h-screen">
        <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

        <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""}`}>
          <AdminHeader setIsOpen={setIsOpen} />

          <main className="p-4 sm:p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-semibold">{TEXT.noAccess}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading || !request) {
    return (
      <div className="flex bg-[#f1f1f1] min-h-screen">
        <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

        <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""}`}>
          <AdminHeader setIsOpen={setIsOpen} />

          <main className="p-4 animate-pulse">
            <div className="max-w-[1040px] mx-auto space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>

              <div className="grid grid-cols-3 gap-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                <div className="h-40 bg-gray-200 rounded"></div>
                <div className="h-40 bg-gray-200 rounded"></div>
              </div>

              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded w-2/3"></div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const instagramUrl = normalizeInstagramLink(request.instagramLink || request.instagramName);

  return (
    <div className="flex bg-[#f1f1f1] min-h-screen">
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""}`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-3 sm:p-5">
          <div className="max-w-[1040px] mx-auto">

            {/* HEADER */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-200"
                >
                  <FaUser className="text-gray-900 text-[18px]" />
                </button>

                <div className="flex items-center gap-2">
                  <FiChevronRight className="text-gray-400" />
                  <h1 className="text-[17px] font-semibold text-gray-900">
                    {request.fullName || TEXT.title}
                  </h1>
                </div>
              </div>

              <div className="relative">
                <button
                  onClick={() => setActionsOpen((v) => !v)}
                  className="
                    actions-button
                    flex items-center gap-1
                    h-8 px-3
                    bg-[#f6f6f7]
                    border border-gray-300
                    rounded-lg
                    shadow-[0_1px_0_rgba(0,0,0,0.04),0_2px_5px_rgba(0,0,0,0.06)]
                    text-sm font-medium text-gray-900
                    hover:bg-gray-100
                    transition
                  "
                >
                  {TEXT.actions}
                  <FiChevronRight className="rotate-90 text-gray-500 text-sm" />
                </button>

                {actionsOpen && (
                  <div className="
                    actions-overlay
                    absolute right-0 top-full mt-1
                    w-44
                    bg-white
                    border border-gray-300
                    rounded-xl
                    shadow-[0_20px_40px_rgba(0,0,0,0.12)]
                    z-50
                  ">
                    <button
                      onClick={() => {
                        setStatusModalOpen(true);
                        setActionsOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      <FaEdit className="text-gray-900 text-[13px]" />
                      {TEXT.changeStatus}
                    </button>
<button
  onClick={() => {
    setActionsOpen(false);
    handleAddAsCustomer();
  }}
  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100"
>
  <FaUserPlus className="text-gray-900 text-[13px]" />
  {TEXT.addAsCustomer}
</button>
                    <div className="border-t my-1"></div>

                    <button
                      onClick={() => {
                        setDeleteId(request.id);
                        setActionsOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <FaTrash className="text-red-600 text-[13px]" />
                      {TEXT.delete}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* STATS */}
            <div className="
              bg-white
              border border-[#e3e3e3]
              rounded-[12px]
              shadow-[0_1px_0_rgba(0,0,0,0.04)]
              mb-4
            ">
              <div
                className={`
                  grid grid-cols-2 sm:grid-cols-3
                  text-sm
                  ${lang === "EN"
                    ? "divide-x divide-gray-200"
                    : "divide-x divide-gray-200 flex-row-reverse"}
                `}
              >
                <div className={`px-4 py-3 flex flex-col justify-center ${lang === "EN" ? "border-r" : "border-l"} border-gray-200`}>
                  <span className="font-semibold text[15px] text-gray-900 mb-[2px]">
                    {TEXT.status}
                  </span>
                  <span className={`inline-flex w-fit px-2.5 py-[2px] rounded-md text-[12px] font-semibold ${statusClass(request.status)}`}>
                    {statusLabel(request.status)}
                  </span>
                </div>

                <div className={`px-4 py-3 flex flex-col justify-center ${lang === "EN" ? "border-r" : "border-l"} border-gray-200`}>
                  <span className="font-semibold text[15px] text-gray-900 mb-[2px]">
                    {TEXT.submittedAt}
                  </span>
                  <span className="font-semibold text-gray-900 text-[13px]">
                    {formatDateTime(request.createdAt)}
                  </span>
                </div>

                <div className="px-4 py-3 flex flex-col justify-center">
                  <span className="font-semibold text[15px] text-gray-900 mb-[2px]">
                    {TEXT.requestSince}
                  </span>
                  <span className="font-semibold text-gray-900 text-[13px]">
                    {formatSince(request.createdAt)}
                  </span>
                </div>
              </div>
            </div>

            {/* TOP CARDS */}
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-3 mb-4 items-start">

              {/* LEFT CARD */}
              <div className="bg-white border border-[#e1e3e5] rounded-[18px] overflow-hidden">
                <div className="flex items-center justify-between px-4 pt-4 mt-1">
                  <h2 className="text-[13px] font-semibold text-[#202223]">
                    {TEXT.requestInfo}
                  </h2>

                  <span className={`text-[12px] font-medium px-2 py-[2px] rounded-md ${statusClass(request.status)}`}>
                    {statusLabel(request.status)}
                  </span>
                </div>

                <div className="p-4 space-y-3 rounded-md border border-gray-200 m-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-[#f6f6f7] rounded-lg p-3">
                      <div className="text-[12px] text-[#6d7175] mb-1">
                        {TEXT.fullName}
                      </div>
                      <div className="text-sm font-semibold text-[#202223]">
                        {request.fullName || "—"}
                      </div>
                    </div>

                    <div className="bg-[#f6f6f7] rounded-lg p-3">
                      <div className="text-[12px] text-[#6d7175] mb-1">
                        {TEXT.phone}
                      </div>
                      <div className="text-sm font-semibold text-[#202223]">
                        {request.phone || "—"}
                      </div>
                    </div>
<div className="bg-[#f6f6f7] rounded-lg p-3">
  <div className="text-[12px] text-[#6d7175] mb-1">
    {TEXT.email}
  </div>
  <div className="text-sm font-semibold text-[#202223]">
    {request.email || "—"}
  </div>
</div>
                    <div className="bg-[#f6f6f7] rounded-lg p-3">
                      <div className="text-[12px] text-[#6d7175] mb-1">
                        {TEXT.businessName}
                      </div>
                      <div className="text-sm font-semibold text-[#202223]">
                        {request.businessName || "—"}
                      </div>
                    </div>

                    <div className="bg-[#f6f6f7] rounded-lg p-3">
                      <div className="text-[12px] text-[#6d7175] mb-1">
                        {TEXT.instagramName}
                      </div>
                      <div className="text-sm font-semibold text-[#202223]">
                        {request.instagramName || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 flex justify-end gap-2">
                  <a
                    href={getWhatsappLink(request.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 h-[34px] bg-white border border-gray-300 rounded-md text-sm shadow hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FaWhatsapp className="text-green-600" />
                    {TEXT.whatsapp}
                  </a>
{request.email && (
  <a
    href={`mailto:${request.email}`}
    className="px-4 h-[34px] bg-white border border-gray-300 rounded-md text-sm shadow hover:bg-gray-50 flex items-center gap-2"
  >
    <FaEnvelope className="text-gray-700" />
    {TEXT.emailButton}
  </a>
)}
                  {instagramUrl ? (
                    <a
                      href={instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 h-[34px] bg-[#202223] text-white rounded-md text-sm hover:bg-black flex items-center gap-2"
                    >
                      <FaInstagram />
                      {TEXT.openInstagram}
                    </a>
                  ) : (
                    <button
                      disabled
                      className="px-4 h-[34px] bg-gray-200 text-gray-400 rounded-md text-sm cursor-not-allowed"
                    >
                      {TEXT.noInstagram}
                    </button>
                  )}
                </div>
              </div>

              {/* RIGHT CARD */}
              <div className="bg-white border border-[#e1e3e5] rounded-[18px] overflow-hidden">
                <div className="p-4 space-y-4">

                  <div>
                    <div className="text-sm font-semibold text-[#202223]">
                      {request.fullName || "—"}
                    </div>

                    <div className="text-[12px] text-[#6d7175]">
                      {request.businessName || "—"}
                    </div>
                  </div>

                  <div>
                    <div className="text-[13px] font-semibold mb-1">
                      {TEXT.contactInfo}
                    </div>

                    <div className="text-sm flex items-center gap-2">
                      <FaPhone className="text-gray-500 text-[12px]" />
                      {request.phone || "—"}
                    </div>
                    <div className="text-sm flex items-center gap-2 mt-1">
  <FaEnvelope className="text-gray-500 text-[12px]" />
  {request.email || "—"}
</div>
                  </div>

                  <div>
                    <div className="text-[13px] font-semibold mb-1">
                      {TEXT.businessInfo}
                    </div>

                    <div className="text-sm flex items-center gap-2">
                      <FaStore className="text-gray-500 text-[12px]" />
                      {request.businessName || "—"}
                    </div>

                    <div className="text-sm flex items-center gap-2 mt-1">
                      <FaInstagram className="text-gray-500 text-[12px]" />
                      {request.instagramName || "—"}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-[13px] font-semibold text-[#202223] mb-2">
                      {TEXT.status}
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClass(request.status)}`}
                    >
                      {statusLabel(request.status)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* TIMELINE */}
            <div className="mt-4">
              <h2 className="text-[15px] font-semibold text-[#202223] mb-5 px-1">
                {TEXT.timeline}
              </h2>

              <div
                className={`
                  relative
                  ${lang === "EN" ? "pl-8 text-left" : "pr-8 text-right"}
                `}
              >
                <div
                  className={`
                    absolute top-2 bottom-2 w-px bg-[#d9d9d9]
                    ${lang === "EN" ? "left-[7px]" : "right-[7px]"}
                  `}
                />

                <div className="relative mb-5">
                  <span
                    className={`
                      absolute ${lang === "EN" ? "left-[-33px]" : "right-[-33px]"}
                      top-[4px]
                      w-[14px] h-[14px]
                      bg-[#e5e5e5]
                      rounded-[4px]
                      flex items-center justify-center
                    `}
                  >
                    <span className="w-[6px] h-[6px] bg-[#202223] rounded-[2px]" />
                  </span>

                  <div className="flex items-baseline justify-between gap-1.5">
                    <p className="text-[14px] text-[#202223] leading-6 flex items-center flex-wrap gap-1.5">
                      <span className="font-medium">
                        {request.fullName}
                      </span>{" "}
                      {TEXT.requestActivity}
                    </p>

                    <span className="text-[11px] text-gray-500 whitespace-nowrap ml-2">
                      {formatDateTime(request.createdAt)}
                    </span>
                  </div>
                </div>

                {request.updatedAt && request.updatedAt !== request.createdAt && (
                  <div className="relative mb-5">
                    <span
                      className={`
                        absolute ${lang === "EN" ? "left-[-33px]" : "right-[-33px]"}
                        top-[4px]
                        w-[14px] h-[14px]
                        bg-[#e5e5e5]
                        rounded-[4px]
                        flex items-center justify-center
                      `}
                    >
                      <span className="w-[6px] h-[6px] bg-[#202223] rounded-[2px]" />
                    </span>

                    <div className="flex items-baseline justify-between gap-1.5">
                      <p className="text-[14px] text-[#202223] leading-6">
                        {TEXT.status}:{" "}
                        <span className="font-medium">
                          {statusLabel(request.status)}
                        </span>
                      </p>

                      <span className="text-[11px] text-gray-500 whitespace-nowrap ml-2">
                        {formatDateTime(request.updatedAt)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {statusModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="w-[380px] bg-white rounded-[14px] shadow-xl overflow-hidden">
                <div className="px-5 py-4 border-b bg-[#f6f6f7]">
                  <h2 className="text-[15px] font-semibold text-[#202223]">
                    {TEXT.changeStatus}
                  </h2>
                </div>

                <div className="px-5 py-5 space-y-2">
                  {["new", "contacted", "approved", "rejected"].map((s) => (
                    <button
                      key={s}
                      onClick={() => handleStatusUpdate(s)}
                      className={`w-full h-10 rounded-lg text-sm font-medium border transition ${
                        request.status === s
                          ? "bg-[#202223] text-white border-[#202223]"
                          : "bg-white border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {statusLabel(s)}
                    </button>
                  ))}
                </div>

                <div className="px-5 py-4 border-t bg-[#f6f6f7] flex justify-end gap-3">
                  <button
                    onClick={() => setStatusModalOpen(false)}
                    className="h-[36px] px-4 border border-[#c9cccf] rounded-lg bg-white text-sm hover:bg-gray-50"
                  >
                    {TEXT.cancel}
                  </button>
                </div>
              </div>
            </div>
          )}

          {deleteId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="w-[380px] bg-white rounded-[14px] shadow-xl overflow-hidden">
                <div className="px-5 py-4 border-b bg-[#f6f6f7] flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 border">
                    <FaTrash className="text-red-600 text-sm" />
                  </div>

                  <h2 className="text-[15px] font-semibold">
                    {TEXT.deleteTitle}
                  </h2>
                </div>

                <div className="px-5 py-5 text-sm text-gray-600">
                  {TEXT.deleteDesc}
                </div>

                <div className="px-5 py-4 border-t flex justify-end gap-3 bg-[#f6f6f7]">
                  <button
                    onClick={() => setDeleteId(null)}
                    className="h-[36px] px-4 border rounded-lg bg-white"
                  >
                    {TEXT.cancel}
                  </button>

                  <button
                    onClick={confirmDelete}
                    className="h-[36px] px-4 bg-red-600 text-white rounded-lg"
                  >
                    {TEXT.delete}
                  </button>
                </div>
              </div>
            </div>
          )}

          {toast && (
            <div className="fixed bottom-6 right-6 z-[9999] animate-fadeSlide">
              <div className="flex items-center gap-3 px-4 py-3 bg-[#202223] text-white rounded-lg shadow text-sm font-medium">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs">
                  ✓
                </div>
                <span>{toast.message}</span>
              </div>
            </div>
          )}

          {isSaving && (
            <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] z-40 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminWholesaleRequestDetails;